import { ReactElement } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
	EuiLoadingSpinner,
	EuiPage,
	EuiPageBody,
	EuiSpacer,
	EuiText,
} from '@equipmentshare/ds2'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import { GridColDef } from '@mui/x-data-grid-premium'
import { heapEvent } from '@equipmentshare/heaper'
import DataGrid from 'views/components/DataGrid'
import { ItemMasterVariantUom, NuminaSerialType, Permission } from '../../../../graphql'
import PermissionsCheck from 'components/PermissionsCheck'
import useItemMasterDetailQuery from './hooks/useItemMasterQuery'
import NuminaReadinessSection from './components/NuminaReadinessSection'
import EquipmentMakeModelLabel from '../components/EquipmentMakeModelLabel'
import { isItemMasterComplete } from '../utils'

function formatNuminaSerialType( type: NuminaSerialType ): string {
	switch ( type ) {
		case NuminaSerialType.Asset: return 'Asset'
		case NuminaSerialType.Bulk: return 'Bulk'
		case NuminaSerialType.Consumable: return 'Consumable'
		default: return type
	}
}

type VariantRow = {
	id: string,
	isBase: boolean,
	uom: ItemMasterVariantUom,
	conversionRate: number,
	freightClass: string | null | undefined,
	nmfc: string | null | undefined,
	shippedInOriginalContainer: boolean,
	siocQuantity: number | null | undefined,
	isPolybagEligible: boolean,
	dimensions: { length: any, width: any, height: any, unit: string } | null | undefined,
	weight: { amount: any, unit: string } | null | undefined,
	productName: string,
	manufacturerNumber: string,
}

function getVariantColumns( onEditVariant: ( variantId: string ) => void ): GridColDef<VariantRow>[] {
	return [
		{
			field: 'isBase',
			headerName: 'Base',
			width: 80,
			renderCell: ( params ) => ( params.row.isBase
				? <Chip label="Base" color="primary" size="small" />
				: null ),
		},
		{
			field: 'uom',
			headerName: 'UoM',
			width: 100,
			valueGetter: ( _value: any, row: VariantRow ) => row.uom,
		},
		{
			field: 'productName',
			headerName: 'Product',
			flex: 2,
			minWidth: 200,
		},
		{
			field: 'manufacturerNumber',
			headerName: 'MFR #',
			width: 120,
		},
		{
			field: 'conversionRate',
			headerName: 'Conversion Rate',
			width: 150,
		},
		{
			field: 'dimensions',
			headerName: 'Dimensions',
			width: 200,
			valueGetter: ( _value: any, row: VariantRow ) => {
				if ( !row.dimensions ) {
					return 'Not set'
				}
				const { length, width, height, unit } = row.dimensions
				return `${length} x ${width} x ${height} ${unit}`
			},
		},
		{
			field: 'weight',
			headerName: 'Weight',
			width: 150,
			valueGetter: ( _value: any, row: VariantRow ) =>
				( row.weight ? `${row.weight.amount} ${row.weight.unit}` : 'Not set' ),
		},
		{
			field: 'freightClass',
			headerName: 'Freight Class',
			width: 130,
			valueGetter: ( _value: any, row: VariantRow ) => row.freightClass ?? '',
		},
		{
			field: 'nmfc',
			headerName: 'NMFC',
			width: 120,
			valueGetter: ( _value: any, row: VariantRow ) => row.nmfc ?? '',
		},
		{
			field: 'shippedInOriginalContainer',
			headerName: 'SIOC',
			width: 80,
			valueGetter: ( _value: any, row: VariantRow ) => ( row.shippedInOriginalContainer ? 'Yes' : 'No' ),
		},
		{
			field: 'siocQuantity',
			headerName: 'SIOC Qty',
			width: 100,
			valueGetter: ( _value: any, row: VariantRow ) => row.siocQuantity ?? '',
		},
		{
			field: 'isPolybagEligible',
			headerName: 'Polybag',
			width: 90,
			valueGetter: ( _value: any, row: VariantRow ) => ( row.isPolybagEligible ? 'Yes' : 'No' ),
		},
		{
			field: 'actions',
			headerName: '',
			width: 60,
			sortable: false,
			filterable: false,
			renderCell: ( params ) => (
				<IconButton size="small" onClick={() => onEditVariant( params.row.id )}>
					<EditIcon fontSize="small" />
				</IconButton>
			),
		},
	]
}

export default function ItemMasterDetail(): ReactElement {
	const { id = '' } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { itemMaster, loading, error } = useItemMasterDetailQuery( id )

	if ( loading ) {
		return (
			<EuiPage paddingSize="l">
				<EuiPageBody>
					<EuiLoadingSpinner size="xl" />
				</EuiPageBody>
			</EuiPage>
		)
	}

	if ( error || !itemMaster ) {
		return (
			<EuiPage paddingSize="l">
				<EuiPageBody>
					<EuiText><p>{error ? error.message : 'Item master not found.'}</p></EuiText>
				</EuiPageBody>
			</EuiPage>
		)
	}

	const baseVariant = itemMaster.variants.find( ( v ) => v.isBase )
	const recordComplete = isItemMasterComplete( itemMaster.variants )
	const productName = baseVariant?.product?.name ?? `Item Master #${itemMaster.id}`
	const manufacturerName = baseVariant?.product && 'manufacturer' in baseVariant.product
		? baseVariant.product.manufacturer?.name ?? null
		: null

	const variantRows: VariantRow[] = itemMaster.variants.map( ( v ) => ( {
		id: v.id,
		isBase: v.isBase,
		uom: v.uom,
		conversionRate: v.conversionRate,
		freightClass: v.freightClass,
		nmfc: v.nmfc,
		shippedInOriginalContainer: v.shippedInOriginalContainer,
		siocQuantity: v.siocQuantity,
		isPolybagEligible: v.isPolybagEligible,
		dimensions: v.dimensions,
		weight: v.weight,
		productName: v.product?.name ?? '',
		manufacturerNumber: ( v.product && 'manufacturerNumber' in v.product ? v.product.manufacturerNumber : '' ) ?? '',
	} ) )

	const handleEditItemMaster = () => {
		heapEvent( 'Inventory - Item Master - Detail - Click - Edit', { item_master_id: id } )
		navigate( `/manage/item-masters/${id}/edit` )
	}

	const handleAddVariant = () => {
		heapEvent( 'Inventory - Item Master - Detail - Click - Add Variant', { item_master_id: id } )
		navigate( `/manage/item-masters/${id}/add-variant` )
	}

	const handleEditVariant = ( variantId: string ) => {
		navigate( `/manage/item-masters/${id}/variants/${variantId}/edit` )
	}

	const variantColumns = getVariantColumns( handleEditVariant )

	const dateCreated = itemMaster.dateCreated
		? new Date( itemMaster.dateCreated ).toLocaleDateString()
		: null
	const dateUpdated = itemMaster.dateUpdated
		? new Date( itemMaster.dateUpdated ).toLocaleDateString()
		: null

	return (
		<EuiPage paddingSize="l">
			<EuiPageBody>
				<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
					<Box mb={3}>
						<Breadcrumbs sx={{ mb: 2 }}>
							<Link
								underline="hover"
								color="inherit"
								sx={{ cursor: 'pointer' }}
								onClick={() => navigate( '/manage/item-masters' )}
							>
							Item Master
							</Link>
							<Typography color="text.primary">{productName}</Typography>
						</Breadcrumbs>

						<Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
							<Box>
								<Stack direction="row" alignItems="center" spacing={1}>
									<Typography variant="h5" fontWeight={600}>{productName}</Typography>
									{!recordComplete && (
										<Chip label="Draft" color="default" size="small" />
									)}
								</Stack>
								{manufacturerName && (
									<Typography variant="body1" color="text.secondary">{manufacturerName}</Typography>
								)}
							</Box>
							<Stack direction="row" spacing={1}>
								<PermissionsCheck permissionsToCheck={[ Permission.CanEditItemMaster ]}>
									<Button variant="outlined" onClick={handleEditItemMaster}>
									Edit Item Master
									</Button>
								</PermissionsCheck>
								<PermissionsCheck permissionsToCheck={[ Permission.CanEditItemMaster ]}>
									<Button variant="outlined" onClick={handleAddVariant}>
									Add Variant
									</Button>
								</PermissionsCheck>
							</Stack>
						</Stack>

						{!recordComplete && (
							<Alert severity="info" sx={{ mb: 2 }}>
							This item master is a draft. Add weight and dimensions to the base variant to complete it. Until then it cannot be added to a distribution center or used for fulfillment and put-away, and warehouse automation (Numina) will not act on it.
							</Alert>
						)}

						<Stack direction="row" spacing={4} flexWrap="wrap">
							<Box>
								<Typography variant="caption" display="block" color="text.secondary">Status</Typography>
								<Typography variant="body2">{itemMaster.isActive ? 'Active' : 'Inactive'}</Typography>
							</Box>
							<Box>
								<Typography variant="caption" display="block" color="text.secondary">Flammable</Typography>
								<Typography variant="body2">{itemMaster.flammable ? 'Yes' : 'No'}</Typography>
							</Box>
							<Box>
								<Typography variant="caption" display="block" color="text.secondary">Lithium Ion Battery</Typography>
								<Typography variant="body2">{itemMaster.lithiumIonBattery ? 'Yes' : 'No'}</Typography>
							</Box>
							<Box>
								<Typography variant="caption" display="block" color="text.secondary">Hazmat</Typography>
								<Typography variant="body2">{itemMaster.isHazmat ? 'Yes' : 'No'}</Typography>
							</Box>
							<Box>
								<Typography variant="caption" display="block" color="text.secondary">Numina Serial Type</Typography>
								<Typography variant="body2">
									{itemMaster.numinaSerialType === NuminaSerialType.Asset ? (
										<>
											{formatNuminaSerialType( itemMaster.numinaSerialType )}
											{( itemMaster.equipmentMakeId || itemMaster.equipmentModelId ) && (
												<>
													{' - '}
													<EquipmentMakeModelLabel
														makeId={itemMaster.equipmentMakeId ? Number( itemMaster.equipmentMakeId ) : null}
														modelId={itemMaster.equipmentModelId ? Number( itemMaster.equipmentModelId ) : null}
													/>
												</>
											)}
										</>
									) : (
										formatNuminaSerialType( itemMaster.numinaSerialType )
									)}
								</Typography>
							</Box>
							<Box>
								<Typography variant="caption" display="block" color="text.secondary">Fragile</Typography>
								<Typography variant="body2">{itemMaster.fragile ? 'Yes' : 'No'}</Typography>
							</Box>
							{dateCreated && (
								<Box>
									<Typography variant="caption" display="block" color="text.secondary">Created</Typography>
									<Typography variant="body2">{dateCreated}</Typography>
								</Box>
							)}
							{dateUpdated && (
								<Box>
									<Typography variant="caption" display="block" color="text.secondary">Updated</Typography>
									<Typography variant="body2">{dateUpdated}</Typography>
								</Box>
							)}
						</Stack>
					</Box>

					<EuiSpacer />

					<Box sx={{ flexShrink: 0 }}>
						<Typography variant="h6" mb={2}>Variants</Typography>
						<DataGrid
							rows={variantRows}
							columns={variantColumns}
							initialState={{ pinnedColumns: { right: [ 'actions' ] } }}
						/>
					</Box>

					<EuiSpacer />

					<Box sx={{ flex: 1, minHeight: 320, display: 'flex', flexDirection: 'column' }}>
						<NuminaReadinessSection
							itemMasterId={itemMaster.id}
							incomplete={!recordComplete}
							variants={itemMaster.variants.map( ( v ) => ( {
								id: v.id,
								isBase: v.isBase,
								uom: v.uom,
								productId: v.product?.id ?? null,
							} ) )}
						/>
					</Box>
				</Box>
			</EuiPageBody>
		</EuiPage>
	)
}
